// `useSpeechCapture()` — the capture machine wired to the driver, for a
// screen to render. No markup: the child view and the aide view render the
// same `state` differently.
//
// react-useeffect-discipline: start, stop and redo happen in the event
// handlers the screen calls (§ 1.5); the tick timer is created by the
// transition into `recording`, not by an effect keyed on state; and the one
// effect is the unmount cleanup that turns the mic off (§ 3.1).
import type {
  SpeechCaptureData,
  SpeechCaptureOutcome,
} from "@bindery/logging/types";
import { useEffect, useRef, useState } from "react";
import {
  type CaptureError,
  type CaptureLimits,
  DEFAULT_CAPTURE_LIMITS,
  type ItemRef,
} from "../contract";
import { type CaptureDriver, startCapture } from "./driver";
import {
  type CaptureEvent,
  type CaptureState,
  initialCaptureState,
  reduceCapture,
} from "./machine";
import { type Capability, detectCapability } from "./negotiate";

// Frequent enough that the auto-stop lands within a quarter second of
// `maxDurationMs`; the warn cue is not a countdown that needs finer steps.
const TICK_MS = 250;

// Structurally satisfied by `Logger` from `@bindery/logging`. Narrowed to
// the one event this hook emits so its `data` is checked against the
// vocabulary's permitted fields; the app's `useLogger()` supplies it,
// because that hook is app-owned (apps/retells/src/logging/).
export interface CaptureLogger {
  event(name: "speech.capture.complete", data: SpeechCaptureData): void;
}

export interface UseSpeechCaptureOptions {
  // Fixed for the hook's lifetime; remount with a `key` to change them.
  readonly limits?: CaptureLimits;
  readonly logger?: CaptureLogger;
  // Log context only — read when a capture starts, so the terminal event
  // describes the attempt that was recorded, not whatever is current when
  // the recorder finishes.
  readonly attempt?: number;
  readonly itemKind?: ItemRef["kind"];
}

export interface SpeechCapture {
  // Computed once, at first render, without prompting. A screen can show
  // an adult-facing microphone notice before `start` ever runs.
  readonly capability: Capability;
  readonly state: CaptureState;
  readonly limits: CaptureLimits;
  // Call from a click/tap handler only: it may raise the permission prompt.
  // No-op unless the state admits a start (idle, blocked, rejected).
  readonly start: () => void;
  // No-op unless recording.
  readonly stop: () => void;
  // Discard a recorded or rejected capture and return to idle.
  readonly redo: () => void;
}

type LogContext = Pick<SpeechCaptureData, "attempt" | "item_kind">;

const outcomeOf = (error: CaptureError): SpeechCaptureOutcome => {
  switch (error.kind) {
    case "unsupported":
      return "unsupported";
    case "permission-denied":
      return "denied";
    case "no-microphone":
      return "no_microphone";
    case "microphone-busy":
      return "busy";
    case "too-short":
      return "too_short";
    case "too-large":
      return "too_large";
    case "recorder-failed":
      return "failed";
  }
};

// "audio/webm;codecs=opus" → "webm". The mime passed the contract's
// AudioMime in the driver, so the subtype is one of the four containers.
const containerOf = (mime: string): SpeechCaptureData["container"] => {
  const subtype = mime.slice("audio/".length).split(";")[0];
  return subtype === "webm" ||
    subtype === "mp4" ||
    subtype === "ogg" ||
    subtype === "wav"
    ? subtype
    : "unknown";
};

// The terminal event for a transition, if it ends a capture. Kinds, sizes
// and timings only — never the audio or anything derived from its content.
const completionOf = (
  next: CaptureState,
  context: LogContext,
): SpeechCaptureData | null => {
  switch (next.tag) {
    case "recorded": {
      const { meta } = next.recording;
      return {
        outcome: "recorded",
        audio_duration_ms: meta.durationMs,
        bytes: meta.bytes,
        container: containerOf(meta.mime),
        ...context,
      };
    }
    case "rejected":
      return {
        outcome: outcomeOf(next.error),
        ...(next.error.kind === "too-short"
          ? { audio_duration_ms: next.error.durationMs }
          : {}),
        ...(next.error.kind === "too-large" ? { bytes: next.error.bytes } : {}),
        ...context,
      };
    case "blocked":
      return { outcome: outcomeOf(next.error), ...context };
    default:
      return null;
  }
};

export const useSpeechCapture = (
  options: UseSpeechCaptureOptions = {},
): SpeechCapture => {
  const [capability] = useState(detectCapability);
  const [limits] = useState(() => options.limits ?? DEFAULT_CAPTURE_LIMITS);
  const [state, setState] = useState(() => initialCaptureState(capability));

  // The machine's current state, readable synchronously by driver callbacks
  // and timers that outlive the render that created them.
  const stateRef = useRef(state);
  const driverRef = useRef<CaptureDriver | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const logRef = useRef<{
    readonly logger: CaptureLogger | undefined;
    readonly context: LogContext;
  } | null>(null);

  const clearTick = (): void => {
    if (tickRef.current !== null) clearInterval(tickRef.current);
    tickRef.current = null;
  };

  // Every transition goes through here, and every side effect follows from
  // the (prev, next) pair — so a stop, a timer or a log line cannot happen
  // without the state that justifies it. Reads only refs and stable setters,
  // so a stale copy captured by a driver callback behaves identically.
  const dispatch = (event: CaptureEvent): void => {
    const prev = stateRef.current;
    const next = reduceCapture(prev, event, limits);
    if (next === prev) return;
    stateRef.current = next;
    setState(next);

    if (
      next.tag === "requesting-permission" &&
      capability.tag === "supported"
    ) {
      logRef.current = {
        logger: options.logger,
        context: {
          ...(options.attempt === undefined
            ? {}
            : { attempt: options.attempt }),
          ...(options.itemKind === undefined
            ? {}
            : { item_kind: options.itemKind }),
        },
      };
      driverRef.current = startCapture(capability.mime, {
        onStarted: (at) => dispatch({ type: "permission-granted", at }),
        onStartFailed: (error) =>
          dispatch({ type: "permission-failed", error }),
        onStopped: (recording) =>
          dispatch({ type: "recorder-stopped", recording }),
        onFailed: () => dispatch({ type: "recorder-failed" }),
      });
    }
    if (next.tag === "recording" && prev.tag !== "recording") {
      tickRef.current = setInterval(
        () => dispatch({ type: "tick", at: performance.now() }),
        TICK_MS,
      );
    }
    if (next.tag !== "recording") clearTick();
    if (next.tag === "stopping") driverRef.current?.stop(next.endedBy);

    const completion = completionOf(next, logRef.current?.context ?? {});
    if (completion !== null) {
      driverRef.current = null;
      logRef.current?.logger?.event("speech.capture.complete", completion);
      logRef.current = null;
    }
  };

  // The one effect: unmounting mid-capture must turn the mic off and stop
  // the timer. dispose() also silences the driver's callbacks, so nothing
  // dispatches into an unmounted component.
  useEffect(
    () => () => {
      clearTick();
      driverRef.current?.dispose();
      driverRef.current = null;
    },
    [],
  );

  return {
    capability,
    state,
    limits,
    start: () => dispatch({ type: "start-requested" }),
    stop: () => dispatch({ type: "stop-requested" }),
    redo: () => dispatch({ type: "redo-requested" }),
  };
};
